import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

import type {
  LineItemTraffickingColumnsFragment,
  LineItemV5,
  Maybe,
  UpdateLineItemInputV5,
  UpdateLineItemMutation,
  UpdateLineItemMutationVariables,
} from '../../../../apis/graphql';
import { campaignClient } from '../../../../apis/graphql';
import type { EditableInputVariant } from '../../../../common/EditableTableCells/EditableCell/EditableCell';
import { getOldCacheData } from '../../../../common/utils';
import { CacheEntityIdentifier } from '../../../../configs/cache';
import { LINE_ITEM_TRAFFICKING_COLUMNS } from './fragments';
import { UPDATE_TRAFFICKING_PAGE_LINE_ITEM } from './mutations';
import useCellVariant from './useCellVariant';
import { onHandleLineItemUpdateErrors } from './utils';

export type UseUpdateLineItem = {
  cellVariant: EditableInputVariant;
  updateLineItemField: (newValue: UpdateLineItemInputV5[keyof UpdateLineItemInputV5]) => void;
  errors: string[];
  loading: boolean;
  data?: Maybe<LineItemTraffickingColumnsFragment>;
};

function calculateDeliveryGoal(goal: Maybe<number>, effectiveBufferPercent: Maybe<number>): Maybe<number> {
  if (goal == null) return null;
  if (effectiveBufferPercent == null) return goal;
  return Math.ceil(goal * (1 + effectiveBufferPercent / 100));
}

const LINE_ITEM_UPDATE_FAILED_MESSAGE = 'Line Item Save Failed';

const useUpdateLineItem = (
  lineItemId: string,
  inputFieldName: keyof UpdateLineItemInputV5,
  onFailureUpdate?: ({ message, description }: Record<string, string>) => void
): UseUpdateLineItem => {
  const [graphQLErrors, setGraphQLErrors] = useState<string[]>([]);
  const [updateLineItem, mutationResult] = useMutation<UpdateLineItemMutation, UpdateLineItemMutationVariables>(
    UPDATE_TRAFFICKING_PAGE_LINE_ITEM
  );

  const cellVariant = useCellVariant(mutationResult);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const updateLineItemField = useCallback(
    async (newValue: UpdateLineItemInputV5[typeof inputFieldName]) => {
      // we need this data because after update we can't receive some fields from campaign-manager
      // according to bug AX-3785
      const { cacheData, cacheId } = getOldCacheData<LineItemV5>({
        cacheIdPrefix: CacheEntityIdentifier.LineItemV5,
        id: lineItemId,
        fragment: LINE_ITEM_TRAFFICKING_COLUMNS,
      });

      try {
        const data = await updateLineItem({
          variables: {
            id: lineItemId,
            updateLineItemInput: {
              [inputFieldName]: newValue,
            },
          },
          update(_, { data }) {
            if (!data || !data.updateLineItem) {
              return;
            }

            const {
              startDate,
              endDate,
              metrics,
              displayPublisherTarget,
              pacingMetrics,
              countries,
              magniteDealDto,
              orderLineItemDto,
              hasZeroDeliveryRisk,
              exclusiveBlock,
              metadata,
              subStatus,
              ssp,
              deal,
              dealMetadata,
              cpEligible,
              automatedDeliveryBufferPercent,
              goal,
              deliveryBufferPercent: cachedDeliveryBufferPercent,
              staqError,
            } = cacheData;

            const getUpdatedDeliveryGoal = (): Maybe<number> => {
              if (inputFieldName === 'automateBuffer') {
                const effectiveBuffer =
                  newValue && automatedDeliveryBufferPercent
                    ? automatedDeliveryBufferPercent
                    : cachedDeliveryBufferPercent ?? null;
                return calculateDeliveryGoal(goal ?? null, effectiveBuffer);
              }

              if (inputFieldName === 'deliveryBufferPercent') {
                return calculateDeliveryGoal(goal ?? null, newValue as number);
              }

              return data?.updateLineItem?.deliveryGoal ?? cacheData.deliveryGoal ?? null;
            };

            const updatedData = {
              ...data?.updateLineItem,
              startDate,
              endDate,
              metrics,
              displayPublisherTarget,
              pacingMetrics,
              countries,
              magniteDealDto,
              orderLineItemDto,
              hasZeroDeliveryRisk,
              exclusiveBlock,
              metadata,
              subStatus,
              ssp,
              deal,
              dealMetadata,
              cpEligible,
              automatedDeliveryBufferPercent,
              staqError,
              deliveryGoal: getUpdatedDeliveryGoal(),
            };

            campaignClient.writeFragment({
              id: cacheId,
              fragment: LINE_ITEM_TRAFFICKING_COLUMNS,
              fragmentName: 'lineItemTraffickingColumns',
              data: updatedData,
              variables: {
                id: lineItemId,
                updateLineItemInput: {
                  [inputFieldName]: newValue,
                },
              },
            });
          },
        });

        if (data && data.errors?.length) {
          onHandleLineItemUpdateErrors({
            message: LINE_ITEM_UPDATE_FAILED_MESSAGE,
            onSetGraphQLErrors: setGraphQLErrors,
            errors: data.errors,
            onFailureUpdate: onFailureUpdate,
          });
        } else {
          setGraphQLErrors([]);
        }
      } catch (error) {
        onHandleLineItemUpdateErrors({
          message: LINE_ITEM_UPDATE_FAILED_MESSAGE,
          onSetGraphQLErrors: setGraphQLErrors,
          error: error as ApolloError,
          onFailureUpdate: onFailureUpdate,
        });
      }
    },
    [inputFieldName, lineItemId, onFailureUpdate, updateLineItem]
  );

  return {
    cellVariant,
    updateLineItemField,
    errors: graphQLErrors,
    loading: mutationResult.loading,
    data: mutationResult.data?.updateLineItem,
  };
};

export default useUpdateLineItem;
